import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  IconButton,
  Typography, 
  Button,
  Stack
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { themeColors } from '../styles/theme';
import { 
  Add as AddIcon,
  Share as ShareIcon,
  Close as CloseIcon
} from '@mui/icons-material';

// Type augmentation for global objects
declare global {
  interface Navigator {
    standalone?: boolean;
  }
  
  interface Window {
    MSStream?: any;
  }
}

// Use styled components for consistent button styling (matching Home.tsx)
const PrimaryButton = styled(Button)(({ theme }) => ({
  background: 'transparent',
  color: '#000',
  fontSize: '1.1rem',
  fontFamily: "ApfelGrotezkRegular",
  borderRadius: '48px',
  transition: 'all 0.2s ease',
  '&:hover': {
    background: themeColors.lightYellow,
  },
  padding: '4px',
  backgroundColor: themeColors.lager,
}));

// Constants for prompt timing
const PROMPT_STORAGE_KEY = 'pwa_install_prompt_last_shown';
const PROMPT_DISMISSED_KEY = 'pwa_install_prompt_dismissed';
const DAYS_BETWEEN_PROMPTS = 7; // Show the prompt once a week if previously dismissed

// Function to detect if user is on a desktop device
const isDesktopDevice = (): boolean => {
  // Check if device has coarse pointer (touchscreen) and hover capability
  const hasCoarsePointer = window.matchMedia('(pointer: coarse)').matches;
  const hasHoverCapability = window.matchMedia('(hover: hover)').matches;
  
  // Check screen size (typical desktop breakpoint)
  const isLargeScreen = window.innerWidth >= 1024;
  
  // A device is likely a desktop if it has hover capability, no coarse pointer, and a large screen
  return hasHoverCapability && !hasCoarsePointer && isLargeScreen;
};

const InstallPWA: React.FC = () => {
  const [supportsPWA, setSupportsPWA] = useState<boolean>(false);
  const [promptInstall, setPromptInstall] = useState<any>(null);
  const [isIOS, setIsIOS] = useState<boolean>(false);
  const [showIOSGuide, setShowIOSGuide] = useState<boolean>(false);
  const [isInstalled, setIsInstalled] = useState<boolean>(false);
  const [showPrompt, setShowPrompt] = useState<boolean>(false);
  const [isDesktop, setIsDesktop] = useState<boolean>(false);

  // Detect device type on component mount
  useEffect(() => {
    setIsDesktop(isDesktopDevice());
    
    // Add resize listener to update device type if window size changes
    const handleResize = () => {
      setIsDesktop(isDesktopDevice());
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Determine if we should show the prompt based on timing
  useEffect(() => {
    // Don't show if already installed or on desktop
    if (isInstalled || isDesktop) {
      setShowPrompt(false);
      return;
    }

    // Check if user has explicitly dismissed the prompt
    const dismissed = localStorage.getItem(PROMPT_DISMISSED_KEY);
    const lastShown = localStorage.getItem(PROMPT_STORAGE_KEY);
    
    if (!lastShown) {
      // First time visit - don't show immediately, set timestamp for next visit
      localStorage.setItem(PROMPT_STORAGE_KEY, Date.now().toString());
      setShowPrompt(false);
      return;
    }
    
    if (dismissed === 'true') {
      // User has dismissed before - check if enough time has passed to show again
      const daysSinceLastShown = (Date.now() - parseInt(lastShown)) / (1000 * 60 * 60 * 24);
      
      if (daysSinceLastShown >= DAYS_BETWEEN_PROMPTS) {
        // It's been long enough, show again and update timestamp
        localStorage.setItem(PROMPT_STORAGE_KEY, Date.now().toString());
        setShowPrompt(true);
      } else {
        setShowPrompt(false);
      }
    } else {
      // User hasn't dismissed before, show on second visit
      setShowPrompt(true);
    }
  }, [isInstalled, isDesktop]);

  useEffect(() => {
    // Don't initialize anything if on desktop
    if (isDesktop) {
      return;
    }

    // Check if app is already installed
    if (window.matchMedia('(display-mode: standalone)').matches || 
        window.navigator.standalone === true) {
      setIsInstalled(true);
      return;
    }

    // Check if device is iOS
    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(isIOSDevice);

    // For Android: Listen for beforeinstallprompt event
    const handler = (e: Event) => {
      e.preventDefault();
      setSupportsPWA(true);
      setPromptInstall(e);
    };
    
    window.addEventListener('beforeinstallprompt', handler);

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, [isDesktop]);

  const installApp = async () => {
    if (!promptInstall) return;
    
    promptInstall.prompt();
    
    const { outcome } = await promptInstall.userChoice;
    
    if (outcome === 'accepted') {
      setSupportsPWA(false);
      setIsInstalled(true);
    }
  };

  // Handle dismissing the prompt
  const dismissPrompt = () => {
    setShowPrompt(false);
    localStorage.setItem(PROMPT_DISMISSED_KEY, 'true');
    localStorage.setItem(PROMPT_STORAGE_KEY, Date.now().toString());
  };

  // Don't show anything if app is already installed or on desktop
  if (isInstalled || isDesktop) return null;

  // If we shouldn't show the prompt based on timing logic
  if (!showPrompt) return null;

  // For Android devices
  if (supportsPWA && showPrompt) {
    return (
      <Dialog 
        open={true}
        PaperProps={{
          sx: {
            borderRadius: 3,
            maxWidth: 500,
            px: 1
          }
        }}
      >
        <DialogTitle 
          sx={{ 
            fontWeight: 600,
            color: '#222',
            pb: 1,
            pt: 3
          }}
        >
          Install App
          <IconButton
            aria-label="close"
            onClick={dismissPrompt}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: 'grey.500',
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <DialogContent>
          <DialogContentText sx={{ color: 'text.secondary' }}>
            Add Suntap to your home screen for the best experience! You'll be able to launch it just like any other app.
          </DialogContentText>
        </DialogContent>
        
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Stack 
            direction={{ xs: 'column', sm: 'row' }} 
            spacing={2} 
            width="100%" 
            alignItems="center"
          >
            <PrimaryButton
              onClick={dismissPrompt}
              sx={{
                fontWeight: 500,
                padding: '4px',
                width: { xs: '100%', sm: '40%' },
                minWidth: { xs: '100%', sm: '180px' },
              }}
            >
              Cancel
            </PrimaryButton>
            <PrimaryButton
              onClick={installApp}
              startIcon={<AddIcon />}
              sx={{
                fontWeight: 500,
                padding: '4px',
                width: { xs: '100%', sm: '40%' },
                minWidth: { xs: '100%', sm: '180px' },
                '&:hover': {
                  backgroundColor: themeColors.mainYellow,
                }
              }}
            >
              Install
            </PrimaryButton>
          </Stack>
        </DialogActions>
      </Dialog>
    );
  }

  // For iOS devices
  if (isIOS && showPrompt) {
    return (
      <>
        <Dialog 
          open={!showIOSGuide}
          onClose={dismissPrompt}
          PaperProps={{
            sx: {
              borderRadius: 3,
              maxWidth: 500,
              px: 1
            }
          }}
        >
          <DialogTitle 
            sx={{ 
              fontWeight: 600,
              color: '#222',
              pb: 1,
              pt: 3
            }}
          >
            Install App
            <IconButton
              aria-label="close"
              onClick={dismissPrompt}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
                color: 'grey.500',
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          
          <DialogContent>
            <DialogContentText sx={{ color: 'text.secondary' }}>
              Add Suntap to your home screen for the best experience! You'll be able to launch it just like any other app.
            </DialogContentText>
          </DialogContent>
          
          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Stack 
              direction={{ xs: 'column', sm: 'row' }} 
              spacing={2} 
              width="100%" 
              alignItems="center"
            >
              <PrimaryButton
                onClick={dismissPrompt}
                sx={{
                  fontWeight: 500,
                  padding: '4px',
                  width: { xs: '100%', sm: '40%' },
                  minWidth: { xs: '100%', sm: '180px' },
                }}
              >
                Cancel
              </PrimaryButton>
              <PrimaryButton
                onClick={() => setShowIOSGuide(true)}
                startIcon={<ShareIcon />}
                sx={{
                  fontWeight: 500,
                  padding: '4px',
                  width: { xs: '100%', sm: '40%' },
                  minWidth: { xs: '100%', sm: '180px' },
                  '&:hover': {
                    backgroundColor: themeColors.mainYellow,
                  }
                }}
              >
                Install
              </PrimaryButton>
            </Stack>
          </DialogActions>
        </Dialog>

        <Dialog 
          open={showIOSGuide} 
          onClose={() => setShowIOSGuide(false)}
          PaperProps={{
            sx: {
              borderRadius: 3,
              maxWidth: 500,
              px: 1
            }
          }}
        >
          <DialogTitle 
            sx={{ 
              fontWeight: 600,
              color: '#222',
              pb: 1,
              pt: 3,
              textAlign: 'center',
              fontFamily: "ApfelGrotezkRegular"
            }}
          >
            Install on iOS
            <IconButton
              aria-label="close"
              onClick={() => {
                setShowIOSGuide(false);
                dismissPrompt();
              }}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
                color: 'grey.500',
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          
          <DialogContent>
            <DialogContentText component="div" sx={{ color: 'text.primary' }}>
              <Box sx={{ 
                p: 2, 
                backgroundColor: 'rgba(251, 189, 32, 0.1)', 
                borderRadius: 2,
                border: '1px solid rgba(251, 189, 32, 0.2)',
                mb: 2
              }}>
                <Typography variant="body1" sx={{ mb: 1.5 }}>
                  1. Tap the Share button <ShareIcon sx={{ verticalAlign: 'middle', fontSize: '1.2rem' }} /> at the bottom of your browser
                </Typography>
                <Typography variant="body1" sx={{ mb: 1.5 }}>
                  2. Scroll down and tap "Add to Home Screen"
                </Typography>
                <Typography variant="body1">
                  3. Tap "Add" in the top right corner
                </Typography>
              </Box>
            </DialogContentText>
          </DialogContent>
          
          <DialogActions sx={{ px: 3, pb: 3, alignItems: 'center', justifyContent: 'center' }}>
            <PrimaryButton
              onClick={() => {
                setShowIOSGuide(false);
                dismissPrompt();
              }}
              sx={{
                fontWeight: 500,
                padding: '4px',
                width: { xs: '80%', sm: '40%' },
                minWidth: { xs: '200px', sm: '180px' },
                background: themeColors.mainYellow,
                '&:hover': {
                  background: themeColors.mainYellow,
                  opacity: 0.9,
                }
              }}
            >
              Close
            </PrimaryButton>
          </DialogActions>
        </Dialog>
      </>
    );
  }

  // Return null for unsupported browsers or if not meeting install criteria
  return null;
};

export default InstallPWA;